import { Divider, Drawer, Flex, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { WorkOrderField } from './work-order-field/work-order-field';
import {
  Maintenance,
  MaintenancePriority,
  MaintenanceStatus,
} from '@/services/maintenances-service/maintenances-service.types';
import Dropdown from '../form/dropdown/dropdown';
import styles from './work-order-details-drawer.module.scss';
import { useStatusOptions } from '../maintenances-header/maintenances-header.constants';
import Label from '../form/label/label';
import { TextArea } from '../form/textarea/textarea';
import { Button } from '../button/button';

interface WorkOrderDetailsDrawerProps {
  className?: string;
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (status: MaintenanceStatus, description: string) => void;
  workOrdersFields: Maintenance;
}

export function WorkOrderDetailsDrawer({
  isVisible,
  onClose,
  onSubmit,
  workOrdersFields,
}: WorkOrderDetailsDrawerProps) {
  const { t } = useTranslation(['maintenances', 'common']);
  const statusOptions = useStatusOptions();

  const fieldTitles: Record<string, string> = {
    id: t('maintenances:fields.id'),
    phone: t('maintenances:fields.phone'),
    company_name: t('maintenances:fields.company'),
    has_property: t('maintenances:fields.hasProperty'),
    priority: t('maintenances:fields.priority'),
    created_at: t('maintenances:fields.created'),
    updated_at: t('maintenances:fields.updated'),
    yardi_id: t('maintenances:fields.yardiId'),
    entry_notes: t('maintenances:fields.notes'),
  };

  const getTitle = (key: string): string => {
    return (
      fieldTitles[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  // Priority and the boolean flag arrive as API values; show their labels, not `high` / `true`.
  const formatValue = (key: string, value: unknown): string => {
    if (key === 'priority') {
      return t(`maintenances:priority.${value as MaintenancePriority}`, {
        defaultValue: String(value),
      });
    }
    if (typeof value === 'boolean') return value ? t('common:states.yes') : t('common:states.no');
    return String(value);
  };

  const fieldsToDisplayInForm = ['status', 'description'];

  return (
    <Drawer
      className={styles.drawer}
      title={t('maintenances:drawer.title')}
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={340}
    >
      <Flex gap={3} vertical>
        {Object.entries(workOrdersFields)
          .filter(([key]) => !fieldsToDisplayInForm.includes(key))
          .filter(([key, value]) => !(key === 'yardi_id' && !value))
          .map(([key, value]) => (
            <WorkOrderField title={getTitle(key)} value={formatValue(key, value)} key={key} />
          ))}
      </Flex>

      <Divider style={{ margin: '12px 0 10px' }} />

      {/* TODO Form */}
      <Form
        className={styles.form}
        onFinish={() => {
          onSubmit(workOrdersFields.status, workOrdersFields.description);
        }}
        initialValues={{
          status: workOrdersFields.status,
          description: workOrdersFields.description,
        }}
      >
        <Flex vertical>
          <Form.Item name="status">
            <div>
              <Label text={t('maintenances:fields.status')} />
              <Dropdown
                data-testid="status"
                disabled
                placeholder={t('maintenances:fields.status')}
                options={statusOptions}
                value={workOrdersFields.status}
                className={styles.dropdown}
                onChange={(value) => {
                  workOrdersFields.status = value as MaintenanceStatus;
                }}
              />
            </div>
          </Form.Item>

          <Form.Item name="description">
            <div>
              <Label text={t('maintenances:fields.description')} />
              <TextArea
                data-testid="description"
                disabled
                placeholder={t('maintenances:drawer.descriptionPlaceholder')}
                value={workOrdersFields.description}
                containerClass={styles.descriptionTextAreaContainer}
                className={styles.descriptionTextArea}
                onChange={(event) => {
                  workOrdersFields.description = event.target.value;
                }}
              />
            </div>
          </Form.Item>
        </Flex>

        <Form.Item>
          <Flex gap={10} className={styles.submitButton}>
            <Button onClick={onClose} buttonVariant="secondary" className={styles.closeButton}>
              {t('common:actions.close')}
            </Button>
            <Button
              className={styles.saveButton}
              buttonVariant="primary"
              htmlType="submit"
              disabled
            >
              {t('common:actions.save')}
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
